// `/v1/repo-enablement` (#7802): the enable picker's reads and its one write. Rows are read a page
// at a time and nothing here ever walks an account: at thousands of repos the selection a person
// confirms is the FILTER and a server count, never ids collected in the browser.
//
// A bulk act's call can stop at its time bound with work left (`next_cursor`), so the act is a loop
// here under one `act_id`, summed, and what landed stays reported when a later call fails.

import type { MaintenanceRecipe, RepoBulkAction } from '@developerz/domain';
import { createQuery } from '@tanstack/solid-query';
import { activeAccountId } from './active-account';
import { apiFetch } from './api';
import type { Page } from './paged';
import { SESSION_TOKEN } from './session';

export type RepoEnablementFilter = {
  readonly q?: string;
  readonly language?: string;
  readonly topic?: string;
  readonly visibility?: 'public' | 'private';
  readonly enabled?: boolean;
  readonly active_within_days?: number;
};

export type RepoEnablementRow = {
  readonly id: string;
  readonly full_name: string;
  readonly enabled: boolean;
  readonly private: boolean;
  readonly archived: boolean;
  readonly language: string | null;
  readonly topics: readonly string[];
  readonly pushed_at: string | null;
};

export type RepoEnablementFacets = {
  readonly languages: ReadonlyArray<{ value: string; count: number }>;
  readonly topics: ReadonlyArray<{ value: string; count: number }>;
};

export type RepoBulkRefused = {
  readonly reason: string;
  readonly detail: string;
  readonly count: number;
  readonly repos: readonly string[];
};

export type RepoBulkAnswer = {
  readonly kind: 'answer';
  readonly dry_run: boolean;
  readonly action: RepoBulkAction;
  readonly act_id: string;
  readonly matched: number | null;
  readonly applied: number;
  readonly unchanged: number;
  readonly refused: readonly RepoBulkRefused[];
  readonly failed: { readonly count: number; readonly repos: readonly string[] };
  readonly recipe_not_applied: number;
  readonly setup: {
    readonly queued: number;
    readonly rate_per_minute: number;
    readonly eta_minutes: number;
  } | null;
  readonly next_cursor: string | null;
};

/** A filter change: a key set to `undefined` clears that filter. */
export type RepoEnablementFilterPatch = {
  readonly [K in keyof RepoEnablementFilter]?: RepoEnablementFilter[K] | undefined;
};

/** What a person selected: hand-picked rows, or everything the filter matches. */
export type RepoSelection =
  | { readonly kind: 'ids'; readonly ids: readonly string[] }
  | { readonly kind: 'all_matching' };

/** A hand-picked set's bound, the API's `REPO_BULK_MAX_IDS`: past it, select by filter. */
export const REPO_PICK_MAX_IDS = 1000;

export const REPO_ENABLEMENT_QUERY_KEY = 'repo-enablement';

/** The list's query string. Empty when nothing is filtered. */
export function repoEnablementQueryString(
  filter: RepoEnablementFilter,
  cursor: string | null,
): string {
  const params = new URLSearchParams();
  if (filter.q !== undefined) params.set('q', filter.q);
  if (filter.language !== undefined) params.set('language', filter.language);
  if (filter.topic !== undefined) params.set('topic', filter.topic);
  if (filter.visibility !== undefined) params.set('visibility', filter.visibility);
  if (filter.enabled !== undefined) params.set('enabled', String(filter.enabled));
  if (filter.active_within_days !== undefined) {
    params.set('active_within_days', String(filter.active_within_days));
  }
  if (cursor !== null) params.set('cursor', cursor);
  return params.toString();
}

export type RepoEnablementPageWire = {
  readonly data: RepoEnablementRow[];
  readonly next_cursor: string | null;
  readonly matching: number | null;
};

/** One keyset page; `matching` is the server's count, on the first page only. */
export function useRepoEnablementPage(
  filter: () => RepoEnablementFilter,
  cursor: () => string | null,
  token: string | null = SESSION_TOKEN,
) {
  return createQuery<Page<RepoEnablementRow> & { matching: number | null }>(() => ({
    queryKey: [REPO_ENABLEMENT_QUERY_KEY, 'page', filter(), cursor(), token, activeAccountId()],
    enabled: token !== null,
    queryFn: async () => {
      const qs = repoEnablementQueryString(filter(), cursor());
      const page = await apiFetch<RepoEnablementPageWire>(
        qs === '' ? '/v1/repo-enablement' : `/v1/repo-enablement?${qs}`,
        token,
      );
      return { data: page.data, next_cursor: page.next_cursor, matching: page.matching };
    },
  }));
}

export function useRepoEnablementFacets(token: string | null = SESSION_TOKEN) {
  return createQuery<RepoEnablementFacets>(() => ({
    queryKey: [REPO_ENABLEMENT_QUERY_KEY, 'facets', token, activeAccountId()],
    enabled: token !== null,
    queryFn: () => apiFetch<RepoEnablementFacets>('/v1/repo-enablement/facets', token),
  }));
}

export type RepoBulkRequest = {
  readonly action: RepoBulkAction;
  readonly filter: RepoEnablementFilter;
  readonly selection: RepoSelection;
  /** Required to enable, and refused on a disable by being absent here. */
  readonly recipe?: MaintenanceRecipe;
};

function bulkBody(req: RepoBulkRequest): Record<string, unknown> {
  return {
    bulk_action: req.action,
    filter: req.filter,
    ...(req.selection.kind === 'ids' ? { ids: req.selection.ids } : {}),
    ...(req.recipe === undefined ? {} : { recipe: req.recipe }),
  };
}

const postBulk = (body: Record<string, unknown>, token: string | null): Promise<RepoBulkAnswer> =>
  apiFetch<RepoBulkAnswer>('/v1/repo-enablement/bulk', token, {
    method: 'POST',
    body: JSON.stringify(body),
  });

/** Counts what the act would do. Writes nothing and audits nothing. */
export function dryRunRepoBulk(
  req: RepoBulkRequest,
  token: string | null,
): Promise<RepoBulkAnswer> {
  return postBulk({ ...bulkBody(req), dry_run: true }, token);
}

/** Two calls of one act as one answer: counts add, refusals fold by reason, the pace is the last. */
export function sumRepoBulk(a: RepoBulkAnswer, b: RepoBulkAnswer): RepoBulkAnswer {
  const refused = new Map<string, RepoBulkRefused>();
  for (const r of [...a.refused, ...b.refused]) {
    const held = refused.get(r.reason);
    refused.set(
      r.reason,
      held === undefined
        ? r
        : { ...held, count: held.count + r.count, repos: [...held.repos, ...r.repos] },
    );
  }
  return {
    ...b,
    matched: a.matched ?? b.matched,
    applied: a.applied + b.applied,
    unchanged: a.unchanged + b.unchanged,
    refused: [...refused.values()],
    failed: {
      count: a.failed.count + b.failed.count,
      repos: [...a.failed.repos, ...b.failed.repos],
    },
    recipe_not_applied: a.recipe_not_applied + b.recipe_not_applied,
  };
}

/**
 * The act, to its end. `expectedCount` is the `matched` the person confirmed: the first call
 * refuses when the selection no longer holds it. A later call that throws hands what already
 * landed to `onProgress` first, so the throw never hides work that was done and audited.
 */
export async function runRepoBulk(
  req: RepoBulkRequest,
  expectedCount: number,
  token: string | null,
  onProgress: (sofar: RepoBulkAnswer) => void,
): Promise<RepoBulkAnswer> {
  let total = await postBulk(
    { ...bulkBody(req), dry_run: false, expected_count: expectedCount },
    token,
  );
  onProgress(total);
  while (total.next_cursor !== null) {
    const next = await postBulk(
      { ...bulkBody(req), dry_run: false, act_id: total.act_id, continue_from: total.next_cursor },
      token,
    );
    total = sumRepoBulk(total, next);
    onProgress(total);
  }
  return total;
}
